import { Box, Tooltip, type SxProps, type Theme } from "@mui/material";
import type { Nda } from "../../types/nda";
import {
  isNdaExpired,
  nonCircumventBadgeLabel,
  nonCircumventTooltipTitle,
} from "../../utils/nonCircumvent";

export interface NonCircumventBadgeProps {
  nda?: Nda | null;
  ndaId?: number | null;
  /** Compact styling for dense tables (e.g. pipeline). */
  compact?: boolean;
  sx?: SxProps<Theme>;
  /** Navigate to the deal NDA tab (or `/deals/:id?tab=nda`). */
  onOpenNda?: () => void;
}

export function NonCircumventBadge({
  nda,
  compact = false,
  sx,
  onOpenNda,
}: NonCircumventBadgeProps) {
  const label = compact ? "NC" : nonCircumventBadgeLabel(nda?.expiry_date);
  const tooltip = nonCircumventTooltipTitle(
    nda?.expiry_date,
    nda?.applies_to,
  );
  const expired = isNdaExpired(nda?.expiry_date);

  const openNda = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenNda?.();
  };

  return (
    <Tooltip title={tooltip} placement="right">
      <Box
        component="span"
        role={onOpenNda ? "button" : undefined}
        tabIndex={onOpenNda ? 0 : undefined}
        onClick={onOpenNda ? openNda : undefined}
        onKeyDown={
          onOpenNda
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") openNda(e);
              }
            : undefined
        }
        sx={{
          ...(compact
            ? {
                color: "#fff",
                backgroundColor: expired ? "success.main" : "error.main",
                fontWeight: 700,
                fontSize: 10,
                lineHeight: 1.2,
                px: 0.75,
                py: 0.25,
                borderRadius: "999px",
                display: "inline-block",
              }
            : {
                color: expired ? "success.main" : "error.main",
                fontWeight: 700,
                ml: 2,
                fontSize: "inherit",
              }),
          cursor: onOpenNda ? "pointer" : "default",
          ...(sx as object),
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}
